<template>
  <div class="products-container">
    <base-loader v-if="dataIsLoading" />

    <template v-else>
      <base-nav
        title="Продукты"
        :show-back="true"
        back-fallback="Home"
        :back-fallback-pattern="/\/products/"
      >
        <base-primary-button
          class="nav-btn"
          @click="toggleCreateForm"
        >
          {{ showCreateForm ? "Отмена" : "Создать" }}
        </base-primary-button>
      </base-nav>

      <!-- Форма создания -->
      <transition name="fade-slide">
        <div v-if="showCreateForm" class="card product-form-wrap">
          <h4 class="form-title">Создание продукта</h4>
          <ProductForm
            :vat-kind-options="vatKindOptions"
            :vat-calculation-type-options="vatCalculationTypeOptions"
            :is-loading="isLoading"
            @save="handleCreate"
            @cancel="showCreateForm = false"
          />
        </div>
      </transition>

      <div v-if="products.length === 0" class="no-data">
        Реестр продуктов пуст
      </div>

      <ul v-else class="products-list">
        <BaseSettingsListItemCard
          v-for="product in localProducts"
          :key="product.id"
          :enabled="product.enabled"
          :is-editing="editingProductId === product.id"
          :is-loading="isLoading"
          :created-at="product.created_at"
          :updated-at="product.updated_at"
          edit-title="Редактирование продукта"
          @edit="startEdit(product)"
          @toggle="toggleProduct(product)"
        >
          <template #edit>
            <ProductForm
              :vat-kind-options="vatKindOptions"
              :vat-calculation-type-options="
                vatCalculationTypeOptions
              "
              :initial-data="product"
              :is-loading="isLoading"
              @save="(payload) => handleUpdate(product.id, payload)"
              @cancel="editingProductId = null"
            />
          </template>

          <div class="product-top">
            <span class="product-name">
              {{ product.name || "—" }}
            </span>
            <span class="product-code">{{ product.code }}</span>
          </div>
          <p v-if="product.description" class="product-description">
            {{ product.description }}
          </p>
          <div class="product-details">
            <span class="detail-chip detail-chip--price">
              {{ product.price }} ₽ / {{ product.unit }}
            </span>
            <span class="detail-chip">
              {{ formatVatKind(product.vat_kind) }}
            </span>
            <span
              v-if="
                product.vat_kind === 'with_vat' && product.vat_rate
              "
              class="detail-chip"
            >
              {{ product.vat_rate }}%
            </span>
            <span
              v-if="product.vat_calculation_type"
              class="detail-chip"
            >
              {{
                formatVatCalculationType(product.vat_calculation_type)
              }}
            </span>
          </div>
        </BaseSettingsListItemCard>
      </ul>

      <!-- Пагинация -->
      <pagination-component
        v-if="pagination.pages > 1 && !dataIsLoading"
        :page="pagination.page"
        :pages="pagination.pages"
        :series="pagination.series"
      />
    </template>
  </div>
</template>

<script>
import { mapState, mapActions } from "pinia";
import { useOrderInvoiceProductStore } from "@/stores/orderInvoiceProductStore";
import { useNotificationStore } from "@/stores/notificationStore";

import ProductForm from "@/components/ProductForm.vue";
import PaginationComponent from "@/components/PaginationComponent.vue";

export default {
  name: "ProductsPage",
  components: {
    ProductForm,
    PaginationComponent,
  },
  data() {
    return {
      dataIsLoading: true,
      showCreateForm: false,
      editingProductId: null,
      localProducts: [],
    };
  },
  computed: {
    ...mapState(useOrderInvoiceProductStore, [
      "products",
      "isLoading",
      "vatKindOptions",
      "vatCalculationTypeOptions",
      "pagination",
    ]),
    notificationStore() {
      return useNotificationStore();
    },
  },
  watch: {
    "$route.query.page": {
      immediate: false,
      handler(newPage) {
        this.loadProducts(newPage);
      },
    },
  },
  methods: {
    ...mapActions(useOrderInvoiceProductStore, [
      "getProductsRequest",
      "createProduct",
      "updateProduct",
      "productEnable",
      "productDisable",
      "formatVatKind",
    ]),

    formatVatCalculationType(value) {
      const option = this.vatCalculationTypeOptions.find(
        (o) => o.value === value
      );
      return option ? option.label : value;
    },

    async loadProducts(page) {
      const pageNumber =
        parseInt(page) || parseInt(this.$route.query.page) || 1;
      try {
        this.dataIsLoading = true;
        await this.getProductsRequest({ page: pageNumber });
        this.localProducts = [...this.products];
      } finally {
        this.dataIsLoading = false;
      }
    },

    updateLocalProduct(updatedProduct) {
      const target = this.localProducts.find(
        (p) => p.id === updatedProduct.id
      );
      if (target) {
        Object.keys(target).forEach((key) => delete target[key]);
        Object.assign(target, updatedProduct);
      }
    },

    toggleCreateForm() {
      this.showCreateForm = !this.showCreateForm;
      if (this.showCreateForm) {
        this.editingProductId = null;
      }
    },

    startEdit(product) {
      this.editingProductId = product.id;
      this.showCreateForm = false;
    },

    async handleCreate(payload) {
      try {
        await this.createProduct(payload);
        this.localProducts = [...this.products];
        this.showCreateForm = false;
        this.notificationStore.showSuccess("Продукт создан");
      } catch {
        // ошибка обрабатывается в сторе
      }
    },

    async handleUpdate(id, payload) {
      try {
        await this.updateProduct(id, payload);
        this.localProducts = [...this.products];
        this.editingProductId = null;
        this.notificationStore.showSuccess("Продукт обновлён");
      } catch {
        // ошибка обрабатывается в сторе
      }
    },

    async toggleProduct(product) {
      try {
        if (product.enabled) {
          await this.productDisable(product.id);
          this.updateLocalProduct({ ...product, enabled: false });
          this.notificationStore.showSuccess("Продукт отключён");
        } else {
          await this.productEnable(product.id);
          this.updateLocalProduct({ ...product, enabled: true });
          this.notificationStore.showSuccess("Продукт включён");
        }
      } catch {
        // ошибка обрабатывается в сторе
      }
    },
  },
  async created() {
    await this.loadProducts(this.$route.query.page);
  },
};
</script>

<style scoped>
.products-container {
  flex-grow: 1;
}

.product-form-wrap {
  margin-bottom: var(--base-out-indent);
}

.form-title {
  margin-bottom: var(--base-in-indent);
}

.products-list {
  display: flex;
  flex-direction: column;
  gap: var(--base-out-indent);
  margin-bottom: 15px;
}

.product-top {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}

.product-name {
  font-weight: 600;
  font-size: 17px;
}

.product-code {
  color: #666;
}

.product-description {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
}

.product-details {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.detail-chip {
  display: inline-block;
  padding: 3px 10px;
  background: #e8e8e8;
  border-radius: 20px;
  font-size: 13px;
  color: #374151;
}

.detail-chip--price {
  background: #ecfdf5;
  color: #065f46;
  font-weight: 500;
}
</style>
